import { useEffect, useState } from 'react';
import { useWeb3React } from '@web3-react/core';
import EthDater from 'ethereum-block-by-date';
import useRaffles from './useRaffles';
import useRainbows from '../hooks/useRainbows';
import { ZERO } from '../util';

const BUFFER = 300; // 5 minutes

const useRaffleEntries = (raffleKey, raffleAccount) => {
  const { library } = useWeb3React();
  const raffle = useRaffles(raffleKey);
  const rainbows = useRainbows();

  const [transactions, setTransactions] = useState({});
  const [entries, setEntries] = useState({});

  const [ready, setReady] = useState(false);

  const [raffleStart, setRaffleStart] = useState();
  const [raffleEnd, setRaffleEnd] = useState();

  const [startBlock, setStartBlock] = useState();
  const [endBlock, setEndBlock] = useState();

  useEffect(() => {
    setReady(false);
    let ignore = false;

    if (library) {
      const raffleStart = new Date(raffle.start_time);
      const raffleEnd = new Date(raffle.end_time);
      setRaffleStart(raffleStart);
      setRaffleEnd(raffleEnd);

      const dater = new EthDater(library);

      Promise.all([
        dater.getDate(raffleStart, false),
        dater.getDate(raffleEnd, true),
      ]).then(([startBlock, endBlock]) => {
        if (!ignore) {
          setStartBlock(startBlock.block);
          setEndBlock(endBlock.block);

          setReady(true);
        }
      });
    }

    return () => {
      ignore = false;
    };
  }, [raffle, library]);

  useEffect(() => {
    let ignore = false;
    setTransactions({});
    if (rainbows && ready) {
      const processEntry = async (event) => {
        if (!ignore) {
          const { timestamp } = await event.getBlock();
          if (
            raffleStart &&
            raffleStart.getTime() <= (timestamp + BUFFER) * 1000 &&
            raffleEnd &&
            raffleEnd.getTime() >= (timestamp - BUFFER) * 1000
          ) {
            const { transactionHash } = event;
            setTransactions((transactions) => ({
              ...transactions,
              [transactionHash]: event.args,
            }));
          }
        }
      };

      const raffleEntry = rainbows.filters.Transfer(null, raffleAccount);
      const fetchEntries = () =>
        rainbows
          .queryFilter(raffleEntry, startBlock, endBlock)
          .then((entries) => entries.forEach(processEntry));
      fetchEntries();
      rainbows.on(raffleEntry, (_from, _to, _value, event) =>
        processEntry(event)
      );
      return () => {
        rainbows.removeAllListeners(raffleEntry);
        ignore = true;
      };
    }
  }, [
    rainbows,
    raffleAccount,
    ready,
    raffleStart,
    raffleEnd,
    startBlock,
    endBlock,
  ]);

  useEffect(() => {
    setEntries({});
    Object.values(transactions).forEach((event) => {
      const { from, value } = event;
      setEntries((entries) => ({
        ...entries,
        [from]: (entries[from] || ZERO).add(value),
      }));
    });
  }, [transactions]);

  return entries;
};

export default useRaffleEntries;
